import { useEffect, useState } from 'react';
import api from '../lib/api';
import usePlanFeatures from './usePlanFeatures';

// Module-level cache so the prefs aren't re-fetched on every nav.
// Cleared on logout via clearAiPrefsCache() (called from AuthContext).
let _cache = null;
let _inflight = null;
const _listeners = new Set();

export function clearAiPrefsCache() {
  _cache = null;
  _listeners.forEach((fn) => fn(null));
}

export function setAiPrefsCache(next) {
  _cache = next;
  _listeners.forEach((fn) => fn(next));
}

/**
 * Reads the workspace's AI preferences. Combined with usePlanFeatures so
 * a caller can ask `prefs.enabled('ask_mantle')` and get the right
 * boolean regardless of whether the gate is plan OR preference.
 *
 * Returns:
 *   loading, prefs (raw), enabled(key) — true only when plan includes
 *   AI AND the specific toggle is on.
 */
export default function useAiPrefs() {
  const { has, loading: planLoading } = usePlanFeatures();
  const [state, setState] = useState({ prefs: _cache, loading: _cache === null });

  useEffect(() => {
    const onChange = (next) => setState({ prefs: next, loading: next === null });
    _listeners.add(onChange);
    if (_cache === null) {
      if (!_inflight) {
        _inflight = api.get('/ai/preferences').then(({ data }) => {
          _cache = data;
          _listeners.forEach((fn) => fn(data));
        }).catch(() => {
          // Default-deny on error so we don't show AI surfaces that
          // would just 403 anyway.
          _cache = { plan_has_ai: false };
          _listeners.forEach((fn) => fn(_cache));
        }).finally(() => { _inflight = null; });
      }
    }
    return () => _listeners.delete(onChange);
  }, []);

  const loading = planLoading || state.loading;
  // While the plan map loads we optimistically allow AI surfaces; the
  // backend still enforces. Once the prefs load we honor the toggle.
  const enabled = (key) => {
    if (loading) return has('ai');
    if (!state.prefs?.plan_has_ai) return false;
    const flag = `${key}_enabled`;
    // If a known key isn't in the prefs doc, default to ON.
    return state.prefs[flag] !== false;
  };

  const save = async (patch) => {
    const { data } = await api.put('/ai/preferences', patch);
    setAiPrefsCache(data);
    return data;
  };

  return {
    loading,
    prefs: state.prefs || {},
    enabled,
    save,
  };
}
